import React, { useEffect } from 'react';
import NoAvatar from '@assets/images/no-avatar.png';
import dayjs from 'dayjs';
import { getResidentACPTagColorAndText, getMentalCapacityColor } from './utils';

import Pagination from '../pagination';

const ResidentTable = ({
  data,
  currentPage,
  setCurrentPage,
  totalPages,
}: {
  data: any[];
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalPages: number;
}) => {
  const rowClassName = (record: any) => {
    if (record.user_profile && record.user_profile.is_migrated) {
      return 'row-pink';
    }
    return '';
  };

  const redirectToPatientDetails = (slug: string) => {
    return `/patient/${slug}`;
  };
  const redirectToPatientNotes = (slug: string) => {
    return `/patient/${slug}/notes`;
  };

  return (
    <div className="resident-table mt-10">
      <h1 className="govuk-heading-l">Progress Table</h1>
      <p className="govuk-body">
        The Progress Table displays the current Advance Care Planning case load.
        This table displays those being cared for.
      </p>
      <p className="govuk-body">
        Individuals are listed in chronological order from the date they were
        added. In other words, the latest addition is displayed at the top. When
        someone&apos;s care is transferred, they are discharged or upon death,
        their listing will no longer be displayed. Their records can still be
        retrieved when entering their name using the &apos;Record Search&apos;
        tool.
      </p>
      <table className="govuk-table govuk-!-static-margin-0">
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            <th
              scope="col"
              className="govuk-table__header govuk-!-width-one-seventh govuk-!-text-align-centre"
            >
              {' '}
            </th>
            <th
              scope="col"
              className="govuk-table__header govuk-!-width-two-sevenths govuk-!-text-align-centre"
            >
              {' '}
            </th>
            <th
              scope="col"
              className="govuk-table__header govuk-!-width-one-seventh govuk-!-text-align-centre"
            >
              Completion Date
            </th>
            <th
              scope="col"
              className="govuk-table__header govuk-!-width-one-seventh govuk-!-text-align-centre"
            >
              Mental Capacity
            </th>
            <th
              scope="col"
              className="govuk-table__header govuk-!-width-one-seventh govuk-!-text-align-centre"
            >
              Conversations with Next of Kin
            </th>
            <th
              scope="col"
              className="govuk-table__header govuk-!-width-one-seventh govuk-!-text-align-centre"
            >
              Last Shared
            </th>
          </tr>
        </thead>
        <tbody className="govuk-table__body">
          {data?.map((resident, index) => {
            return (
              <tr
                className={`govuk-table__row ${rowClassName(resident)}`}
                key={resident.id || index}
              >
                <td className="govuk-table__cell img-cell">
                  <a href={redirectToPatientNotes(resident.user_profile.slug)}>
                    {resident.user_profile.avatar ? (
                      <img
                        width="300"
                        height="300"
                        className="resident-img"
                        src={resident.user_profile.avatar}
                        alt={resident.user_profile.full_name}
                      />
                    ) : (
                      <img
                        width="300"
                        height="300"
                        className="resident-img"
                        src={NoAvatar}
                        alt={resident.user_profile.full_name}
                      />
                    )}
                  </a>
                </td>
                <td className="govuk-table__cell">
                  <h3 className="govuk-heading-s">
                    {resident.user_profile.full_name}
                  </h3>
                  <p className="govuk-body">
                    <a
                      href={redirectToPatientNotes(resident.user_profile.slug)}
                      className="govuk-link govuk-link--no-visited-state"
                    >
                      Details
                    </a>
                  </p>
                  <p className="govuk-body">
                    <a
                      href={redirectToPatientDetails(
                        resident.user_profile.slug
                      )}
                      className="govuk-link whitespace-nowrap govuk-link--no-visited-state"
                    >
                      Care record
                    </a>
                  </p>
                </td>

                <td className="govuk-table__cell govuk-!-text-align-centre">
                  {(() => {
                    const { color, displayText } =
                      getResidentACPTagColorAndText(resident);
                    return (
                      <strong
                        className={`govuk-tag ${color ? `govuk-tag--${color}` : ''}`}
                      >
                        {displayText}
                      </strong>
                    );
                  })()}
                </td>

                <td className="govuk-table__cell govuk-!-text-align-centre">
                  {(() => {
                    const mentalCapacityColor = getMentalCapacityColor(
                      resident.mentalCapacity
                    );
                    return (
                      <strong
                        className={`govuk-tag ${mentalCapacityColor ? `govuk-tag--${mentalCapacityColor}` : ''}`}
                      >
                        {resident.mentalCapacity}
                      </strong>
                    );
                  })()}
                </td>
                <td className="govuk-table__cell govuk-!-text-align-centre">
                  {(() => {
                    const conversationNfkText =
                      resident?.user_profile?.conversation_at;
                    return (
                      <strong
                        className={`govuk-tag ${conversationNfkText ? 'govuk-tag--green' : ''}`}
                      >
                        {conversationNfkText
                          ? dayjs(conversationNfkText).format('DD/MM/YYYY')
                          : ''}
                      </strong>
                    );
                  })()}
                </td>
                <td className="govuk-table__cell govuk-!-text-align-centre">
                  {(() => {
                    const lastSharedText = resident.user_profile.share_acp_at;
                    return (
                      <strong
                        className={`govuk-tag ${lastSharedText ? 'govuk-tag--green' : ''}`}
                      >
                        {lastSharedText
                          ? dayjs(lastSharedText).format('DD/MM/YYYY')
                          : ''}
                      </strong>
                    );
                  })()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </div>
  );
};

export default ResidentTable;
